import { z } from "zod";

import { safeSchema } from "./safeSchema";

const unexpectedSymbol = Symbol("unexpectedEnumValue");

type EnumSchema = z.ZodEnum<[string, ...Array<string>]>;
type EnumOrUnionSchema = EnumSchema | UnionSchema;
type UnionSchema = z.ZodUnion<
  Readonly<[EnumOrUnionSchema, EnumOrUnionSchema, ...Array<EnumOrUnionSchema>]>
>;

type EnumDef = z.ZodEnumDef<[string, ...Array<string>]>;
type UnionDef = z.ZodUnionDef<
  [EnumOrUnionSchema, EnumOrUnionSchema, ...Array<EnumOrUnionSchema>]
>;
type EnumOrUnionDef = EnumDef | UnionDef;

/**
 * Creates a flexible schema that can parse strings against a union of enum
 * schemas.
 *
 * A value the frontend does not know about is kept as `raw` instead of failing
 * the parse, so one unrecognized member never rejects the whole response. This
 * matters most for a list endpoint, where a single row carrying a value the
 * backend added after this build shipped would otherwise fail the array's parse
 * and empty the whole page. The caller decides how to render the raw value.
 *
 * Ported from Zeffiroso (`packages/utils/zod/safeEnumSchema.ts`) with two
 * changes: it imports `zod` rather than `zod/v3`, so this package uses the same
 * zod instance as the rest of the repo, and the symbol description spells
 * `unexpected` in full.
 *
 * ```ts
 * const colorSchema = safeEnumSchema(z.enum(["Red", "Green", "Blue"]));
 * colorSchema.parse("Red"); // { expected: true, value: "Red" }
 * colorSchema.parse("Yellow"); // { expected: false, value: safeEnumSchema.unexpected, raw: "Yellow" }
 * ```
 */
function safeEnumSchemaInternal<
  TOutput extends string = string,
  TDef extends EnumOrUnionDef = EnumOrUnionDef,
  TInput = TOutput,
>(schema: z.ZodType<TOutput, TDef, TInput>) {
  return safeSchema(
    schema,
    /**
     * Should always be a string.
     */
    z.string(),
  ).transform((result) => {
    const ret:
      | {
          expected: true;
          value: TOutput;
        }
      | {
          expected: false;
          value: typeof unexpectedSymbol;
          raw: string;
        } = result.isSuccess
      ? {
          expected: true,
          value: result.data,
        }
      : {
          expected: false,
          value: unexpectedSymbol,
          raw: result.raw,
        };
    return ret;
  });
}

const safeEnumSchema = Object.assign(safeEnumSchemaInternal, {
  unexpected: unexpectedSymbol,
});

export { safeEnumSchema };
